/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType, HVSRecord} from '@ion/core/src/types/entities';
import {getSpeedByStatus} from '@ion/dls/src/maps/helpers/MapViewUtils';

/** Returns a memoized speed value getter that only exposes speed when the
  * asset's current translated state differs from the specified target state key.
  * Otherwise it returns an empty string.
  * 
  * ```typescript
  * const getSpeed = useAssetSpeed();
  * 
  * const speed = getSpeed(asset, speed, targetState);
  * 
  * ```
  **/
export const useAssetSpeed = () => {
  const getSpeed = useCallback((asset: CVSAssetType | HVSRecord, speed: number, targetStateKey: string = 'Parked') => {
    const currentStateKey = getAssetState(asset);
    const currentState    = translate(`common:assetState.${currentStateKey}`);
    const targetState     = translate(`common:assetState.${targetStateKey}`);

    return getSpeedByStatus(
      speed,
      currentState,
      targetState
    );
  }, []);

  return getSpeed;
};
